import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ErrorBoundary — React コンポーネントの例外をキャッチして
 * ホワイトスクリーンを防ぎ、フォールバック UI を表示する
 *
 * 配置: <App> 全体をラップ。さらに重要な領域 (エディタ等) にも個別ラップ可能
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
    // 必要なら外部 telemetry (Sentry 等) に送信
    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
      try { window.Sentry.captureException(error, { extra: errorInfo }); } catch {}
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <ErrorOutlineIcon sx={{ fontSize: 64, color: '#dc2626', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          画面の表示中に問題が発生しました
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mb: 3 }}>
          ご迷惑をおかけして申し訳ございません。<br />
          ページを再読み込みすると改善する可能性があります。
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleReload}>
            再読み込み
          </Button>
          <Button variant="outlined" onClick={this.handleHome}>
            ホームへ
          </Button>
        </Box>
        {process.env.NODE_ENV !== 'production' && this.state.error && (
          <Box sx={{ textAlign: 'left', mt: 3, p: 2, bgcolor: '#fef2f2', borderRadius: 1, fontSize: '0.7rem' }}>
            <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.75rem' }}>Dev only — Error Detail:</Typography>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.7rem', margin: 0 }}>
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          </Box>
        )}
      </Container>
    );
  }
}

export default ErrorBoundary;
